import { ArrowBack } from '@mui/icons-material';
import {
  Box,
  Grid,
  IconButton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { ReactNode, useMemo } from 'react';
import { StyledPaper } from '../constants/styles';
import { t } from '../constants/translations';
import { useLocale } from '../contexts/locale.context';
import useMedia from '../utils/useMedia';
import Loader from './Loader';

export type baseRow = {
  id: string;
  order?: number;
};

export type Column<T extends baseRow> = {
  name: string;
  title: string;
  render?: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  cellProps?: TableCellProps;
  filterable?: boolean;
  hideOnMobile?: boolean;
  sx?: SxProps;
};

type Props<T extends baseRow> = {
  title?: string;
  rows: T[];
  columns: Column<T>[];
  actionButton?: ReactNode;
  customHeader?: ReactNode;
  searchBy?: keyof T;
  onRowClick?: (row: T) => void;
  onGoBack?: () => void;
  onOrderChange?: (row: T, direction: 'up' | 'down') => Promise<void>;
  isCard?: boolean;
  showHeader?: boolean;
  isGrid?: boolean;
  page: number;
  perPage?: number;
  count: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
};

const DataTablePaginated = <T extends baseRow>({
  title,
  rows,
  columns: columnsData,
  actionButton,
  searchBy,
  customHeader,
  isCard = true,
  showHeader = true,
  onRowClick,
  onGoBack,
  onOrderChange,
  isGrid,
  page,
  perPage = 10,
  count,
  onPageChange,
  loading,
}: Props<T>) => {
  const { lang } = useLocale();
  const { isMobile } = useMedia();

  const columns = useMemo(
    () => columnsData.filter((c) => c.render !== undefined && (!isMobile || !c.hideOnMobile)),
    [columnsData, isMobile]
  );

  const hasTop = useMemo(
    () => Boolean(onGoBack || customHeader || searchBy || actionButton),
    [onGoBack, customHeader, searchBy, actionButton]
  );

  if (isGrid) {
    return (
      <Grid container>
        {rows.map((r) => (
          <Grid
            item
            key={r.id}
            xs={4}
            sm={6}
            md={4}
            lg={3}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              alignSelf: 'flex-start',
              justifyContent: 'center',
              textAlign: 'center',
              gap: 0.5,
              p: 1,
              borderRadius: 1,
              cursor: 'pointer',
              ':hover': {
                bgcolor: (t) => `${t.palette.primary.light}11`,
              },
            }}
            onClick={onRowClick ? () => onRowClick(r) : undefined}
          >
            {columns.map((col) => (col.render ? <div key={col.name}>{col.render(r)}</div> : null))}
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <>
      {title && (
        <Typography variant="h6" sx={{ mb: 2, ml: 2 }}>
          {title}
        </Typography>
      )}

      {hasTop && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {onGoBack && (
              <IconButton onClick={onGoBack}>
                <ArrowBack />
              </IconButton>
            )}

            {customHeader}
          </Box>

          {actionButton}
        </Box>
      )}

      <StyledPaper isCard={isCard}>
        {!loading && rows.length === 0 && (
          <Box sx={{ textAlign: 'center', px: 5, py: 8 }}>
            <Typography sx={{ color: (t) => t.palette.text.disabled, mb: 2 }}>{t.there_is_nothing[lang]}</Typography>

            {actionButton}
          </Box>
        )}
        <TableContainer>
          <Table sx={{ width: '100%' }}>
            <>
              {showHeader && !!rows.length && (
                <TableHead>
                  <TableRow>
                    {!!onOrderChange && rows.length > 1 && <TableCell sx={{ p: 0 }} />}

                    {columns.map((col, i) => (
                      <TableCell key={col.name || undefined} align={col.align || 'right'} sx={col.sx}>
                        {col.title}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
              )}

              <TableBody>
                {!loading &&
                  rows.map((row) => (
                    <TableRow
                      key={row.id}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      sx={{
                        cursor: onRowClick ? 'pointer' : 'inherit',
                        ':hover': {
                          bgcolor: (t) => `${t.palette.primary.light}11`,
                        },
                      }}
                    >
                      {columns.map((col) => {
                        const value = col.render ? col.render(row) : null;

                        return (
                          <TableCell key={col.name} align={col.align || 'right'} sx={col.sx}>
                            {typeof value === 'string' || typeof value === 'number' ? (
                              <Typography>{value}</Typography>
                            ) : (
                              value
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}

                {loading && (
                  <TableRow>
                    <TableCell colSpan={columns.length}>
                      <Loader open />
                    </TableCell>
                  </TableRow>
                )}

                {!!rows.length && (
                  <TableRow>
                    <TablePagination
                      count={count}
                      rowsPerPage={perPage}
                      rowsPerPageOptions={[perPage]}
                      page={page}
                      onPageChange={(_, p) => onPageChange(p)}
                      labelDisplayedRows={({ page, count }) => `${page + 1}/${Math.ceil(count / perPage)}`}
                    />
                  </TableRow>
                )}
              </TableBody>
            </>
          </Table>
        </TableContainer>
      </StyledPaper>
    </>
  );
};

export default DataTablePaginated;
